import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Sparkles, ArrowRight } from 'lucide-react';

const SMILEY_LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_121ad1ad-d724-4658-ab95-7dc4ce7b4a83/artifacts/766c9fb51cb57f3d_image.png";
const HEART_LOGO = "https://customer-assets.emergentagent.com/job_crookid-shop/artifacts/xaiwzyng_Crookid%201.jpeg";

const About = () => {
  useEffect(() => { document.title = 'About | Crookid Smilez'; }, []);
  return (
    <div className="fade-in" data-testid="about-page">
      {/* Hero */}
      <section className="bg-cs-dark border-b border-cs-border py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs text-cs-ochre uppercase tracking-[0.3em] mb-3">Est. 2024</p>
          <h1 className="font-graffiti text-5xl md:text-7xl uppercase text-cs-text leading-none mb-6" data-testid="about-title">
            Our Story
          </h1>
          <p className="font-mono text-lg text-cs-muted max-w-xl">
            We believe smiles don't have to be perfect to be powerful.
          </p>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-16 sm:py-20 bg-cs-concrete">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-anton text-3xl sm:text-4xl uppercase text-cs-text mb-6 tracking-tight">
                What Does "Crookid" Mean?
              </h2>
              <p className="font-mono text-sm text-cs-muted leading-relaxed mb-4">
                We started Crookid Smilez because we were tired of the "perfect life" narrative. 
                You know the one — where everyone's got it together, every photo is filtered, 
                and struggle is something you hide.
              </p>
              <p className="font-mono text-sm text-cs-muted leading-relaxed mb-4">
                But here's the truth: <span className="text-cs-ochre font-semibold">the best smiles are earned</span>. They come after 
                the tough days, the setbacks, the moments when you wanted to quit but didn't.
              </p>
              <p className="font-mono text-sm text-cs-muted leading-relaxed">
                A "crookid smile" isn't crooked because something's wrong. 
                It's crooked because it's <span className="text-cs-ochre font-semibold">real</span>. It's the smile that says, 
                "Yeah, life got messy, but I'm still here."
              </p>
            </div>
            <div className="relative flex items-center justify-center">
              <img src={SMILEY_LOGO} alt="Crookid Smilez Logo" className="w-full max-w-sm mx-auto drop-shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 sm:py-20 bg-cs-bg2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs text-cs-ochre uppercase tracking-[0.3em] mb-2 text-center">Core Values</p>
          <h2 className="font-anton text-3xl sm:text-4xl uppercase text-center text-cs-text mb-12 tracking-tight">
            What We Stand For
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-cs-card border border-cs-border p-8 text-center hover:border-cs-ochre transition-colors">
              <div className="w-14 h-14 mx-auto mb-6 bg-cs-ochre flex items-center justify-center">
                <Heart className="w-7 h-7 text-cs-dark" />
              </div>
              <h3 className="font-anton text-xl uppercase mb-3 text-cs-text">Resilience</h3>
              <p className="font-mono text-xs text-cs-muted leading-relaxed">
                Life knocks you down? Get back up. That's not toxic positivity — that's survival.
              </p>
            </div>
            <div className="bg-cs-card border border-cs-border p-8 text-center hover:border-cs-ochre transition-colors">
              <div className="w-14 h-14 mx-auto mb-6 bg-cs-ochre flex items-center justify-center">
                <Users className="w-7 h-7 text-cs-dark" />
              </div>
              <h3 className="font-anton text-xl uppercase mb-3 text-cs-text">Community</h3>
              <p className="font-mono text-xs text-cs-muted leading-relaxed">
                Thinkers who apply all facts before basing a decision off emotions.
              </p>
            </div>
            <div className="bg-cs-card border border-cs-border p-8 text-center hover:border-cs-ochre transition-colors">
              <div className="w-14 h-14 mx-auto mb-6 bg-cs-ochre flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-cs-dark" />
              </div>
              <h3 className="font-anton text-xl uppercase mb-3 text-cs-text">Relentless</h3>
              <p className="font-mono text-xs text-cs-muted leading-relaxed">
                Fact checkers who wanna know the truth and won't base their reality from other people's opinions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Heart/Pause */}
      <section className="py-16 sm:py-20 bg-cs-concrete">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 flex items-center justify-center">
              <div className="bg-cs-card border border-cs-border p-8 md:p-12">
                <img src={HEART_LOGO} alt="Heart Pause Logo" className="w-32 h-32 mx-auto" />
              </div>
            </div>
            <div className="order-1 md:order-2">
              <h2 className="font-anton text-3xl sm:text-4xl uppercase text-cs-text mb-6 tracking-tight">
                The Heart With Pause
              </h2>
              <p className="font-mono text-sm text-cs-muted leading-relaxed mb-4">
                You've seen our heart with the pause symbol inside. It's more than a logo — it's a reminder.
              </p>
              <p className="font-mono text-sm text-cs-muted leading-relaxed mb-4">
                Sometimes you need to <span className="text-cs-ochre font-semibold">pause</span>. Take a breath. Check in with yourself. 
                Mental health isn't a weakness — it's essential.
              </p>
              <p className="font-mono text-sm text-cs-muted leading-relaxed">
                The heart represents love — for yourself, your family, your community. 
                The pause represents the hesitation to just believe without facts of experience or evidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We're For */}
      <section className="py-16 sm:py-20 bg-cs-bg2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs text-cs-ochre uppercase tracking-[0.3em] mb-2 text-center">The Squad</p>
          <h2 className="font-anton text-3xl sm:text-4xl uppercase text-center text-cs-text mb-12 tracking-tight">
            Who We're For
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Family', desc: 'Individuals from newborns to OG\'s who use their differences or uniqueness as a strength.' },
              { title: 'Athletes', desc: 'Every player in the game of life who understands everybody is playing but it\'s not a game, or it\'s a game and nobody is playing.' },
              { title: 'Creatives', desc: 'Artists and dreamers who turn struggle into fuel.' },
              { title: 'Fighters', desc: 'Anybody who had to smile through a situation which you knew wasn\'t right or gonna end in your favor.' },
            ].map((item) => (
              <div key={item.title} className="bg-cs-card border border-cs-border p-6 hover:border-cs-olive2 transition-colors">
                <h3 className="font-anton text-lg uppercase mb-2 text-cs-ochre">{item.title}</h3>
                <p className="font-mono text-xs text-cs-muted leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-cs-olive/10 border-y border-cs-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-anton text-3xl sm:text-4xl uppercase text-cs-text mb-4 tracking-tight">
            Ready to Join the Movement?
          </h2>
          <p className="font-mono text-sm text-cs-muted mb-8">
            Wear your resilience. Have the day you deserve.
          </p>
          <Link to="/shop" className="inline-flex items-center gap-2 bg-cs-olive text-cs-text font-mono uppercase tracking-wider text-sm px-8 py-4 hover:bg-cs-olive2 transition-colors" data-testid="shop-cta">
            Shop Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
